/**
 * OnboardingApiClient — the surface the flow hooks depend on.
 *
 * The default export (``defaultOnboardingApiClient``) wires through to the
 * standard user-onboarding endpoints. Feature consumers that want to reuse
 * the onboarding hooks against a different backend (e.g. the admin-issued
 * enrollment invitation flow) can construct their own client matching this
 * shape and pass it to ``useOnboardingFlow({ api, ... })``.
 */

import * as userOnboardingApi from '@/features/onboarding/api/onboarding-api';
import type { OnboardingFlowResponse } from '@/features/onboarding/types/onboarding.types';

/**
 * Raw alternative shape returned by the onboarding/alternatives endpoint.
 *
 * ``factorType`` is a string at the transport boundary — the flow hook coerces
 * it to ``FactorType`` when it maps into state. Kept as a loose type here so
 * different backends (onboarding vs. enrollment-invitation) can implement the
 * same interface without a shared cast.
 */
export interface OnboardingApiAlternative {
  factorType: string;
  isEnrolled: boolean;
  displayName: string;
  description: string;
}

export interface OnboardingApiClient {
  submitFactor(
    payload: Record<string, unknown>
  ): Promise<OnboardingFlowResponse>;
  submitPassword(password: string): Promise<OnboardingFlowResponse>;
  submitSmsCode(code: string): Promise<OnboardingFlowResponse>;
  resendOtp(): Promise<{ success: boolean }>;
  getAuthenticatorAlternatives(): Promise<{
    alternatives: OnboardingApiAlternative[];
  }>;
  chooseAuthenticator(factorType: string): Promise<OnboardingFlowResponse>;
  completeOnboarding(): Promise<OnboardingFlowResponse>;
}

export const defaultOnboardingApiClient: OnboardingApiClient = {
  submitFactor: userOnboardingApi.submitFactor,
  submitPassword: userOnboardingApi.submitPassword,
  submitSmsCode: userOnboardingApi.submitSmsCode,
  resendOtp: userOnboardingApi.resendOtp,
  getAuthenticatorAlternatives: userOnboardingApi.getAuthenticatorAlternatives,
  chooseAuthenticator: userOnboardingApi.chooseAuthenticator,
  completeOnboarding: userOnboardingApi.completeOnboarding,
};
